/**
 * What the assistant can see of the forms on screen, and the one way it can touch them.
 *
 * A form that registers here can be read (its fields and current values) and filled — by the
 * shell, on the person's click, never by the model directly. The SDK's `Form` registers itself, and
 * so does the shell's action dialog; a hand-written editor that holds its values in its own state
 * is not fillable until it registers with `useAssistForm` itself, and the assistant is told so
 * rather than left to guess.
 *
 * Forms stack: a lookup's «Add …» opens a dialog over the dialog it came from, and the top one is
 * the one the person is looking at. Each registration reads its values through a ref updated on
 * every render, so the shell never fills a form with a setter from the render it mounted in.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
const stack = [];
const filled = new Map();
const listeners = new Set();
let version = 0;
function changed() {
    version++;
    for (const l of listeners)
        l();
}
export function registerAssistForm(form) {
    stack.push(form);
    changed();
    return () => {
        const i = stack.lastIndexOf(form);
        if (i >= 0)
            stack.splice(i, 1);
        filled.delete(form.id);
        changed();
    };
}
/** The form the person is looking at: the last one opened that is still open. */
export function topAssistForm() {
    return stack[stack.length - 1] ?? null;
}
export function assistForms() {
    return stack;
}
/** Which fields the assistant filled in this form, for the marks drawn around them. */
export function markAssistFilled(formId, keys) {
    const set = filled.get(formId) ?? new Set();
    for (const k of keys)
        set.add(k);
    filled.set(formId, set);
    changed();
}
export function assistFilled(formId) {
    return filled.get(formId) ?? new Set();
}
export function subscribeAssist(listener) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}
/** Re-render when a form opens, closes or is filled. */
export function useAssistVersion() {
    return useSyncExternalStore(subscribeAssist, () => version, () => version);
}
let panelOpen = false;
/**
 * Whether the assistant's panel is open. A modal dialog makes everything outside it inert — the
 * panel included — so while the panel is open a form dialog opens without trapping focus, and the
 * person can read the assistant, apply what it proposed, and pick a field in the form.
 */
export function setAssistPanelOpen(open) {
    if (panelOpen === open)
        return;
    panelOpen = open;
    changed();
}
export function assistPanelOpen() {
    return panelOpen;
}
export function useAssistPanelOpen() {
    return useSyncExternalStore(subscribeAssist, () => panelOpen, () => false);
}
let pressedAt = 0;
/** The panel was pressed. Called on pointer-down, before anything the press does. */
export function markAssistPress() {
    pressedAt = Date.now();
}
/**
 * An event on the assistant's panel, or while it is picking — not a click "outside" a dialog.
 * A dialog may ask only once the click has landed, by which time the button pressed can be gone
 * ("Apply" becomes "Applied"); a detached target just after a press on the panel was the panel's.
 */
export function isAssistEvent(target) {
    if (typeof document !== 'undefined' && document.documentElement.hasAttribute('data-assist-picking'))
        return true;
    if (target instanceof Element && target.closest('[data-assistant]'))
        return true;
    return target instanceof Element && !target.isConnected && Date.now() - pressedAt < 1500;
}
let counter = 0;
/**
 * Register a form for as long as it is mounted. `values` and `set` may change on every render;
 * what the shell calls is always the latest.
 */
export function useAssistForm(options) {
    const latest = useRef(options);
    latest.current = options;
    const initial = useRef(JSON.stringify(options.values));
    const enabled = options.enabled !== false;
    useEffect(() => {
        if (!enabled)
            return;
        const id = `form-${++counter}`;
        initial.current = JSON.stringify(latest.current.values);
        return registerAssistForm({
            id,
            get title() { return latest.current.title; },
            get fields() { return latest.current.fields; },
            values: () => latest.current.values,
            set: (key, value) => latest.current.set(key, value),
            dirty: () => JSON.stringify(latest.current.values) !== initial.current,
            root: () => latest.current.root(),
        });
    }, [enabled]);
}
/**
 * Whether a field is one the assistant must not read or fill: a secret by its type or its name.
 * The same words the setup conversation refuses to ask for.
 */
export function isSecretField(field) {
    if (field.type === 'password' || field.type === 'secret')
        return true;
    return /pass(word|code)?|secret|token|otp|pin\b|iban|card|cvv|كلمة|رمز|سري|بطاقة|آيبان/i.test(`${field.key} ${field.label ?? ''}`);
}
/**
 * Whether a proposed value is one this field can hold, and the value as the control keeps it.
 * A model's "12" for a number becomes 12; a choice has to be one of the choices, by value or by
 * its label; anything else is refused with the reason, which goes back to the model.
 */
export function checkAssistValue(field, raw) {
    if (!field)
        return { ok: false, reason: 'unknown' };
    if (isSecretField(field))
        return { ok: false, reason: 'secret' };
    const type = field.type ?? 'text';
    if (raw === null)
        return { ok: true, value: null };
    switch (type) {
        case 'file':
            return { ok: false, reason: 'file' };
        case 'number':
        case 'integer': {
            const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw.replace(/,/g, '')) : NaN;
            if (!Number.isFinite(n) || (type === 'integer' && !Number.isInteger(n)))
                return { ok: false, reason: 'type' };
            return { ok: true, value: n };
        }
        case 'boolean':
            if (typeof raw === 'boolean')
                return { ok: true, value: raw };
            if (raw === 'true' || raw === 'false')
                return { ok: true, value: raw === 'true' };
            return { ok: false, reason: 'type' };
        case 'date':
            return typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? { ok: true, value: raw } : { ok: false, reason: 'type' };
        case 'time':
            return typeof raw === 'string' && /^\d{2}:\d{2}$/.test(raw) ? { ok: true, value: raw } : { ok: false, reason: 'type' };
        case 'select': {
            const chosen = choiceOf(field, raw);
            return chosen === undefined ? { ok: false, reason: 'choice' } : { ok: true, value: chosen };
        }
        case 'multiselect': {
            const list = Array.isArray(raw) ? raw : [raw];
            const chosen = list.map((v) => choiceOf(field, v));
            return chosen.some((v) => v === undefined) ? { ok: false, reason: 'choice' } : { ok: true, value: chosen };
        }
        default: {
            if (typeof raw !== 'string' && typeof raw !== 'number')
                return { ok: false, reason: 'type' };
            const text = String(raw);
            return text.length > 5000 ? { ok: false, reason: 'type' } : { ok: true, value: text };
        }
    }
}
function choiceOf(field, raw) {
    const options = field.options ?? [];
    if (typeof raw !== 'string' && typeof raw !== 'number' && typeof raw !== 'boolean')
        return undefined;
    const byValue = options.find((o) => String(o.value) === String(raw));
    if (byValue)
        return byValue.value;
    const byLabel = options.find((o) => o.label.trim().toLowerCase() === String(raw).trim().toLowerCase());
    return byLabel?.value;
}
//# sourceMappingURL=assist.js.map